import { APP_COPY } from '@/copy/app.copy';

/**
 * The five surfaces of the product, per `docs/SPEC.md`.
 *
 * | Surface | Spec | Access |
 * | --- | --- | --- |
 * | Files   | §6  | Everyone with a space grant |
 * | Spaces  | §4  | Admin only |
 * | Users   | §9  | Admin only |
 * | Storage | §10 | Admin only |
 * | Audit   | §8  | Admin only |
 *
 * **Spaces is the newest, and it exists because of who does the act rather
 * than because of a new one.** Creating a room, adding an agency to it, and
 * leaving or removing one (CON-6, CON-14) are administrative, and they used to
 * sit on Files, where the person looking at the screen is usually a deputy who
 * cannot perform any of them. Files kept the drilling-in — see **ADR-0032**.
 *
 * **It was called Agencies until ADR-0071's last consequence landed.** The name
 * was accurate when a row here was a counterpart agency and a "link" was a
 * connection between exactly two of them. A row is now a room with a name, an
 * owner and a roster (CON-22), so the surface was named after its rows' former
 * contents rather than after what it lists.
 *
 * `adminOnly` here drives **navigation visibility only**. It is not an
 * authorization control: SEC-5 requires the API to enforce authorization on
 * every request from the token-resolved identity, and the client's UI state is
 * never an authorization input. Hiding a space is a courtesy, not a boundary.
 */
export interface SurfaceDefinition {
  id: SurfaceId;
  path: string;
  label: string;
  adminOnly: boolean;
  /** Requirement prefix in SPEC.md, for traceability. */
  spec: string;
}

export const SURFACE_IDS = ['files', 'spaces', 'users', 'storage', 'audit'] as const;
export type SurfaceId = (typeof SURFACE_IDS)[number];

export const SURFACES: readonly SurfaceDefinition[] = [
  {
    id: 'files',
    path: '/files',
    label: APP_COPY.nav.files,
    adminOnly: false,
    spec: 'FIL',
  },
  {
    /**
     * First in the Settings domain, which is why the rail's Settings icon and
     * the sidebar wordmark both land here rather than on Users: the rooms an
     * agency shares are the most structural thing its admin configures, and
     * Users is a list of people to grant access to spaces this surface is what
     * creates.
     *
     * `spec: 'CON'` rather than `'FIL'` — the surface exists for §4's spaces
     * work, not for §6's file work, even though the table on it was born there.
     */
    id: 'spaces',
    path: '/spaces',
    label: APP_COPY.nav.spaces,
    adminOnly: true,
    spec: 'CON',
  },
  {
    id: 'users',
    path: '/users',
    label: APP_COPY.nav.users,
    adminOnly: true,
    spec: 'USR',
  },
  {
    id: 'storage',
    path: '/storage',
    label: APP_COPY.nav.storage,
    adminOnly: true,
    spec: 'STO',
  },
  {
    id: 'audit',
    path: '/audit',
    label: APP_COPY.nav.audit,
    adminOnly: true,
    spec: 'AUD',
  },
];

/** Surfaces the given role should see in navigation (see caveat above). */
export function visibleSurfaces(isAdmin: boolean): readonly SurfaceDefinition[] {
  return isAdmin ? SURFACES : SURFACES.filter((surface) => !surface.adminOnly);
}

/**
 * The rail switches between two domains: Files on its own, and everything
 * configurable under Settings. Derived from the URL rather than held as
 * component state, for the same reason as everything else navigation-shaped
 * here (ADR-0007) — the rail and the sidebar must never be able to disagree
 * about which domain is current.
 */
export type Domain = 'files' | 'settings';

export function domainOf(pathname: string): Domain {
  return pathname.startsWith('/files') ? 'files' : 'settings';
}

/**
 * A line in the Settings sidebar.
 *
 * Deliberately not a `SurfaceDefinition`: a surface is one of the five things
 * the product is *about* (SPEC §§4, 6, 8–10), and General, Passkeys, and
 * Signature are about the person looking at it. `SURFACES` stays five entries
 * long and `surfaces.test.ts` still holds that line — what changed is that the
 * sidebar now lists more than surfaces.
 */
export interface SettingsEntry {
  id: string;
  path: string;
  label: string;
  /** Visibility only, exactly as on `SurfaceDefinition` above — see SEC-5. */
  adminOnly: boolean;
}

export interface SettingsGroup {
  id: 'account' | 'admin';
  /** Rendered as an `<Eyebrow>`-style heading above the group. */
  label: string;
  entries: readonly SettingsEntry[];
}

/**
 * Where the rail's Settings icon and the sidebar wordmark both land.
 *
 * **It used to be the spaces surface**, and it moved for one reason: Settings is no
 * longer admin-only (**ADR-0064**), so its landing route cannot be a screen most
 * of the agency is told it may not see. General is first in the sidebar and
 * available to everybody, which makes it the only answer that needs no branch on
 * role — and a landing route that varied by role is exactly the kind of thing
 * the rail and the sidebar would eventually disagree about.
 */
export const SETTINGS_HOME = '/settings/general';

/**
 * The domain's index — every settings page as a grid, at `/settings`.
 *
 * **A second constant rather than a re-pointed `SETTINGS_HOME`**, because the
 * two answer different questions and only one of them is "where does Settings
 * land". The rail's gear, the bottom tab, and the sidebar's wordmark keep
 * landing on General: all switch domains, and sending a domain switch to an
 * index would be a page of links in front of the page they were reaching for.
 *
 * This one is for the reader who has **no** list. `AppShell` renders the
 * sidebar `hidden … md:block`, so under 768px the Settings sub-nav is off the
 * screen. The domain itself is reachable at every width — the rail's gear
 * `sm:flex`, `DomainTabBar` its `sm:hidden` mirror below 640px — but each lands
 * on General beside that hidden sidebar, so a reader on a phone can be in
 * Settings and still have no list. The account menu carries the link here, and
 * this index is that list.
 */
export const SETTINGS_INDEX = '/settings';

/**
 * The account group — settings about the person rather than about the agency.
 *
 * Passkeys and Signature already existed at these paths and were reachable only
 * from the account menu, which is how a person with a passkey to rename found
 * out about them: by being told. They are in the sidebar now, and the account
 * menu still spaces them, because a shortcut to a page is not a competing
 * location for it.
 */
export const ACCOUNT_SETTINGS: readonly SettingsEntry[] = [
  { id: 'general', path: SETTINGS_HOME, label: APP_COPY.nav.general, adminOnly: false },
  { id: 'passkeys', path: '/settings/passkeys', label: APP_COPY.nav.passkeys, adminOnly: false },
  { id: 'signature', path: '/settings/signature', label: APP_COPY.nav.signature, adminOnly: false },
];

/**
 * Agency settings that are **not** surfaces (USR-16, UPL-13, **ADR-0098**).
 *
 * The admin group below is derived from `SURFACES`, which is right for the four
 * things that are surfaces and wrong for anything that is not. Scanners is the
 * first: it is a short read-only list an admin consults occasionally, not a
 * place anybody works, and promoting it to a fifth surface would put it in the
 * rail beside Files for every admin and imply acts this release does not offer.
 *
 * **Keep this list short, and keep the test that says so.** The reason the
 * admin group is derived at all is that a restated surface drifts; a sibling
 * list beside it is the same hazard in miniature, and it earns its place only
 * while everything in it is genuinely not a surface.
 */
export const AGENCY_SETTINGS: readonly SettingsEntry[] = [
  { id: 'scanners', path: '/settings/scanners', label: APP_COPY.nav.scanners, adminOnly: true },
];

/**
 * The Settings sidebar, in order: what you can change about yourself, then what
 * an admin can change about the agency.
 *
 * **The admin group is derived from `SURFACES`, not restated.** Spaces, Users,
 * Storage, and Audit have exactly one definition — path, label, and `adminOnly`
 * all come from there — so a surface that is renamed or re-gated moves here
 * without anybody remembering to.
 *
 * _Amended (USR-16)._ **It is that derivation plus `AGENCY_SETTINGS`**,
 * for the entries that are agency-scoped and are not surfaces. See that list's
 * own header for why the exception is narrow.
 */
export function settingsGroups(isAdmin: boolean): readonly SettingsGroup[] {
  const account: SettingsGroup = {
    id: 'account',
    label: APP_COPY.nav.groups.account,
    entries: ACCOUNT_SETTINGS.filter((entry) => isAdmin || !entry.adminOnly),
  };

  if (!isAdmin) return [account];

  return [
    account,
    {
      id: 'admin',
      label: APP_COPY.nav.groups.admin,
      entries: [
        ...SURFACES.filter((surface) => surface.id !== 'files').map((surface) => ({
          id: surface.id,
          path: surface.path,
          label: surface.label,
          adminOnly: surface.adminOnly,
        })),
        ...AGENCY_SETTINGS,
      ],
    },
  ];
}
